/**
 * Admin Layout — Talk2 light glass sidebar.
 */
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Hash, Activity, Settings, Radio, Shield, Webhook, BarChart3, Megaphone, Globe } from 'lucide-react';
import Header from '../Header';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true, hue: '#007AFF' },
  { to: '/admin/users', icon: Users, label: 'Usuarios', hue: '#5856D6' },
  { to: '/admin/channels', icon: Hash, label: 'Canales', hue: '#34C759' },
  { to: '/admin/roles', icon: Shield, label: 'Roles', hue: '#FF9500' },
  { to: '/admin/organizations', icon: Globe, label: 'Organizaciones', hue: '#30B0C7' },
  { to: '/admin/activity', icon: Activity, label: 'Actividad', hue: '#AF52DE' },
  { to: '/admin/analytics', icon: BarChart3, label: 'Analytics', hue: '#007AFF' },
  { to: '/admin/webhooks', icon: Webhook, label: 'Webhooks', hue: '#FF9F0A' },
  { to: '/admin/broadcasts', icon: Megaphone, label: 'Broadcasts', hue: '#FF375F' },
  { to: '/admin/alerts', icon: Activity, label: 'Alertas', hue: '#FF2D55' },
  { to: '/admin/transcriptions', icon: Hash, label: 'Transcripciones', hue: '#5856D6' },
  { to: '/admin/settings', icon: Settings, label: 'Configuración', hue: '#86868B' },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-screen">
      <Header onMenuToggle={() => setSidebarOpen(o => !o)} />

      <div className="flex flex-1 overflow-hidden">
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-20 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <aside
          className={`
            fixed md:static top-[60px] md:top-auto left-0 bottom-0 md:bottom-auto
            w-60 flex-shrink-0 flex flex-col z-30 md:z-auto
            transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          `}
          style={{
            background: 'rgba(255,255,255,0.78)',
            WebkitBackdropFilter: 'saturate(180%) blur(30px)',
            backdropFilter: 'saturate(180%) blur(30px)',
            borderRight: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <div className="px-4 py-4 flex-shrink-0"
            style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-md flex items-center justify-center shadow-apple-sm"
                style={{ background: 'linear-gradient(135deg,#FF375F,#AF52DE)' }}>
                <Settings size={15} style={{ color: '#fff' }} />
              </div>
              <div>
                <div className="text-sm font-extrabold text-n-800 tracking-apple-tighter">Admin Panel</div>
                <div className="text-[10px] font-semibold text-n-500 uppercase tracking-widest">RocketTalk</div>
              </div>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-1">
            {navItems.map(({ to, icon: Icon, label, end, hue }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-all duration-150 tracking-apple-tight ${
                    isActive
                      ? 'text-white'
                      : 'text-n-700 hover:bg-black/5'
                  }`
                }
                style={({ isActive }) =>
                  isActive
                    ? {
                        background: `linear-gradient(135deg, ${hue}, #AF52DE)`,
                        boxShadow: `0 6px 16px ${hue}55`,
                      }
                    : {}
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={15} style={{ color: isActive ? '#fff' : hue }} />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="p-3 flex-shrink-0">
            <button
              onClick={() => navigate('/')}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-xs font-semibold transition-all"
              style={{ background: '#E6F1FF', color: '#007AFF', border: 'none' }}
            >
              <Radio size={13} />
              ← Volver a RocketTalk
            </button>
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto min-w-0 scrollbar-thin">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
